import { useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material'

// ── helpers ───────────────────────────────────────────────────────────────────

const not = (a, b) => a.filter((v) => !b.some((x) => x.id === v.id))
const intersection = (a, b) => a.filter((v) => b.some((x) => x.id === v.id))

const LIST_MAX_HEIGHT = 200

const cropLabel = (crop) => {
  const name = crop.name || crop.crop_templates?.name || 'Unknown'
  return `${name} ×${crop.count || 1}`
}

const titleSx = {
  fontWeight: 700,
  color: ({ palette }) => palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const CropList = ({ items, checked, onToggle }) => (
  <Paper variant="outlined" sx={{ maxHeight: LIST_MAX_HEIGHT, overflow: 'auto' }}>
    <List dense component="div" role="list" disablePadding>
      {items.length === 0 && (
        <ListItemButton disabled sx={{ py: 0, pl: 0.5, m: 0 }}>
          <ListItemText
            primary={<Typography variant="caption" color="text.disabled">None</Typography>}
          />
        </ListItemButton>
      )}
      {items.map((crop) => {
        const isChecked = checked.some((c) => c.id === crop.id)
        const labelId = `transfer-item-${crop.id}`
        return (
          <ListItemButton
            key={crop.id}
            role="listitem"
            onClick={onToggle(crop)}
            sx={{ py: 0, pl: 0.5, m: 0 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox
                checked={isChecked}
                tabIndex={-1}
                disableRipple
                size="small"
                slotProps={{ input: { 'aria-labelledby': labelId } }}
                sx={{ maxHeight: '20px' }}
              />
            </ListItemIcon>
            <ListItemText
              id={labelId}
              primary={
                <Typography variant="body2" noWrap>
                  {cropLabel(crop)}
                </Typography>
              }
            />
          </ListItemButton>
        )
      })}
    </List>
  </Paper>
)

// ── component ─────────────────────────────────────────────────────────────────

/**
 * Transfer list for selecting which crops carry over to the next season.
 * Left = Archive (removed after season end), Right = Keep (carry over).
 *
 * @param {Object[]} archive - Crops marked for archiving
 * @param {Function} setArchive
 * @param {Object[]} keep - Crops marked to keep
 * @param {Function} setKeep
 */
export const CropTransferList = ({ archive, setArchive, keep, setKeep }) => {
  const [checked, setChecked] = useState([])

  const leftChecked = intersection(checked, archive)
  const rightChecked = intersection(checked, keep)

  const handleToggle = (crop) => () => {
    const exists = checked.some((c) => c.id === crop.id)
    setChecked(exists ? not(checked, [crop]) : [...checked, crop])
  }

  const handleAllRight = () => {
    setKeep([...keep, ...archive])
    setArchive([])
    setChecked(not(checked, archive))
  }

  const handleCheckedRight = () => {
    setKeep([...keep, ...leftChecked])
    setArchive(not(archive, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    setArchive([...archive, ...rightChecked])
    setKeep(not(keep, rightChecked))
    setChecked(not(checked, rightChecked))
  }

  const handleAllLeft = () => {
    setArchive([...archive, ...keep])
    setKeep([])
    setChecked(not(checked, keep))
  }

  return (
    <Box>
      {/* Header row */}
      <Grid
        container
        spacing={1}
        sx={{
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
          borderColor: ({ palette }) => palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
          pb: 0,
          mb: 0.75,
        }}
      >
        <Grid size="grow" textAlign='center'>
          <Typography variant="caption" sx={titleSx}>Archive ({archive.length})</Typography>
        </Grid>
        <Grid sx={{ minWidth: 44 }} />
        <Grid size="grow" textAlign='center'>
          <Typography variant="caption" sx={titleSx}>Keep Next Season ({keep.length})</Typography>
        </Grid>
      </Grid>

      {/* Body */}
      <Grid container spacing={1} sx={{ alignItems: 'center' }}>
        <Grid size="grow">
          <CropList items={archive} checked={checked} onToggle={handleToggle} />
        </Grid>

        <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Button size="small" variant="outlined" onClick={handleAllRight} disabled={archive.length === 0} aria-label="move all right">≫</Button>
            <Button size="small" variant="outlined" onClick={handleCheckedRight} disabled={leftChecked.length === 0} aria-label="move selected right">&gt;</Button>
            <Button size="small" variant="outlined" onClick={handleCheckedLeft} disabled={rightChecked.length === 0} aria-label="move selected left">&lt;</Button>
            <Button size="small" variant="outlined" onClick={handleAllLeft} disabled={keep.length === 0} aria-label="move all left">≪</Button>
          </Box>
        </Grid>

        <Grid size="grow">
          <CropList items={keep} checked={checked} onToggle={handleToggle} />
        </Grid>
      </Grid>
    </Box>
  )
}
